import { loadRecordSnapshot } from "../../../collaboration/loadRecordSnapshot";
import { RecordCollaborationPanel } from "../../../collaboration/RecordCollaborationPanel";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Formik } from "formik";
import ModernCalculationSelector from "../../utils/Selector/ModernCalculationSelector";
import {
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  fetchOfferCalculation,
} from "../../../api/offer-calculation";
import {
  createOfferCalculationAsync,
  updateOfferCalculationAsync,
} from "../../../slices/offer-calculation";
import { getOfferAsync, patchOfferTotals, patchOfferCalculationSnapshot } from "../../../slices/offer";
import CalculationAutosave from "./calculations/calculation-autosave";
import OfferCalculationWorkspace from "./calculations/calculation-workspace";
import CalculationTemplateActions from "./calculations/calculation-template-actions";
import NestedCalculationEditorDialog from "./calculations/nested-calculation-editor-dialog";
import CalculationNestedSourceDialog from "./calculations/calculation-nested-source-dialog";
import OfferPresentationRulesDialog from "./offer-presentation-rules-dialog";
import OfferTemplateWizardDialog from "./offer-template-wizard-dialog";
import { templateNeedsWizard } from "./utils/template-wizard";
import { expandTemplateByUnits } from "./utils/template-expand";
import {
  buildBlankNestedCalculationPayload,
  buildNestedCopyFromTemplatePayload,
  buildReplaceFromTemplateValues,
  ensureNestedLinkedAsItems,
  nestedLineFromCopy as nestedLineFromSavedCopy,
  resolveTitleOnTemplateReplace,
} from "./utils/apply-calculation-template";
import {
  isModuleCalculation,
  isOfferCalculationLocked,
  isOfferCalculationV2Enabled,
  resolveOfferActionErrorMessage,
} from "./utils";
import { readOfferGroupId } from "./utils/offer-calc-item-groups";
import {
  deletedItemTombstones,
  deletedRowKeys,
  mergeSavedItemsAfterPersist,
  preferLocalItemSet,
  stripDeletedCalcItems,
} from "./utils/offer-calc-row-sync";
import {
  adoptSavedOptimisticLockFields,
  applyConflictUseServerResolution,
  calculationOptimisticLockFields,
  ensureClientRowKeys,
  isCalculationConflictError,
  isCalculationConflictUnresolved,
  isValidOfferCalculationSnapshot,
  openCalculationConflict,
  planConflictUseServerAdoption,
  resolveConflictUseLocal,
  runTrailingCalculationPersistQueue,
} from "./utils/offer-calculation-conflict";
import { calculationPersistFingerprint } from "./utils/calculation-persist-fingerprint";
import { registerOfferSave } from "./utils/offer-save-queue";
import { calculationSalesTotal, lineAmount } from "./utils/offer-workspace";
import "../../../pages/dashboard/offers/calculations/calculation.css";

/** One offer has one calculation page. Nested template copies are lines. */
const pageCalculations = (list) => {
  const roots = (Array.isArray(list) ? list : []).filter(
    (calc) => calc?.parent_calculation_id == null
  );
  return roots.length ? [roots[0]] : [];
};

/**
 * `fetchOfferCalculation` returns the template's own items (with the
 * template's item ids). When copying the template into a brand-new
 * offer-specific calculation those ids belong to a different
 * offer_calculation_id, so the create endpoint's `items.find_by_id` would
 * silently no-op instead of copying any lines. Strip ids so every line is
 * created fresh against the new calculation.
 */
const stripItemIdsForCopy = (items) =>
  (items || []).map(
    ({
      id,
      offer_group_id,
      offerGroupId,
      nested_calculation_id,
      nested_calculation,
      ...rest
    }) => ({
      ...rest,
      offer_group_id: null,
      nested_calculation_id: null,
      nested_calculation: undefined,
    })
  );

const stripNestedCalculationIdsForCopy = (nestedCalculations) =>
  (nestedCalculations || []).map(({ id, nested_calculations, items, ...rest }) => ({
    ...rest,
    items: stripItemIdsForCopy(items),
    nested_calculations: stripNestedCalculationIdsForCopy(nested_calculations),
  }));

/**
 * Stable identity for a calculation across create/update round-trips.
 * Falls back to a client-only `_local_key` (not sent to the API — see
 * buildCalculationPayload's explicit field whitelist) rather than
 * `original_calculation_id` alone, so adding the same template twice before
 * either copy has a real id doesn't collide on one save/merge slot.
 */
const calculationKey = (calc) =>
  calc?._local_key
    ? `local:${calc._local_key}`
    : calc?.id != null
    ? `id:${calc.id}`
    : calc?.original_calculation_id != null
    ? `orig:${calc.original_calculation_id}`
    : null;

/**
 * Prefer local offer_group_id (including null) when a soft-sync would otherwise
 * wipe a just-persisted placement during the short hold window after PUT.
 */
function mergeCalculationsPreservingPlacement(localCalcs, serverCalcs) {
  if (!Array.isArray(localCalcs) || !Array.isArray(serverCalcs)) {
    return serverCalcs;
  }
  const localByKey = new Map();
  localCalcs.forEach((calc) => {
    const key = calculationKey(calc);
    if (key) localByKey.set(key, calc);
  });
  return serverCalcs.map((serverCalc) => {
    const key = calculationKey(serverCalc);
    const localCalc = key ? localByKey.get(key) : null;
    if (!localCalc) return serverCalc;
    const mergedItems = preferLocalItemSet(
      localCalc.items || [],
      serverCalc.items || []
    );
    return {
      ...serverCalc,
      items: mergedItems,
      nested_calculations:
        localCalc.nested_calculations ?? serverCalc.nested_calculations,
      _local_key: localCalc._local_key,
    };
  });
}

/**
 * Offer detail ?tab=kalkyle — Claude three-pane workspace for the selected
 * embedded calculation (not a card list + dialog).
 *
 * offer-bug-p0: calculation edits are persisted directly against the owning
 * OfferCalculation (`PUT/POST /offer_calculations`) instead of round-tripping
 * through `PUT /offers/:id` — the Offer controller's strong params never
 * permitted `:calculations`, so that path silently dropped every edit and
 * the follow-up `getOffer` refresh (now removed) was the source of the
 * PUT+GET storm and the "add line disappears" race.
 */
export default function OfferCalculations({ order, formik }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  /**
   * Template import must NOT auto-create Offer::Group chapters on the offer.
   * Structure is explicit (user adds groups / places lines). Lines land ungrouped.
   */
  const resolveTemplateImportGrouping = async (_calcData) => ({
    offerGroupId: null,
    groupIdMap: null,
  });

  const [calculations, setCalculations] = useState(
    () => pageCalculations(order?.calculations)
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [calculationSelectorOpen, setCalculationSelectorOpen] = useState(() => new URLSearchParams(window.location.search).get("start") === "template");
  const [templatePickerMode, setTemplatePickerMode] = useState(() =>
    new URLSearchParams(window.location.search).get("start") === "template" ? "replace" : "line"
  );
  useEffect(() => {
    if (!calculationSelectorOpen) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("start") !== "template") return;
    params.delete("start");
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, "", next);
  }, [calculationSelectorOpen]);
  const [attachTargetRow, setAttachTargetRow] = useState(null);
  const [gridEditorOpen, setGridEditorOpen] = useState(false);
  const [nestedEditorId, setNestedEditorId] = useState(null);
  const [nestedEditorFocusRecommended, setNestedEditorFocusRecommended] =
    useState(false);
  const [calcSaveState, setCalcSaveState] = useState("idle");
  /** Unresolved 409: blocks autosave until user picks local or server. */
  const [calcConflict, setCalcConflict] = useState(null);
  const [collaborationReviewEpoch, setCollaborationReviewEpoch] = useState(0);
  const calcConflictRef = useRef(null);
  // Keep ref in sync every render; also assign synchronously in setters below
  // so unmount/flush cannot race ahead of the next paint.
  calcConflictRef.current = calcConflict;

  const setCalcConflictSync = (next) => {
    calcConflictRef.current =
      typeof next === "function" ? next(calcConflictRef.current) : next;
    setCalcConflict(calcConflictRef.current);
  };
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [sourceChooserOpen, setSourceChooserOpen] = useState(false);
  const [sourceChooserBusy, setSourceChooserBusy] = useState(false);
  const [templateWizard, setTemplateWizard] = useState(null); // { calcData, mode, parent, targetRow }
  const addingFromTemplateRef = useRef(false);
  const hydratedIdsRef = useRef(new Set());
  const ensuringOfferIdRef = useRef(null);

  const featureFlags =
    order?.offer_feature_flags || formik?.values?.offer_feature_flags || {};
  const calculationV2Enabled = isOfferCalculationV2Enabled(featureFlags);
  const calculationLocked = isOfferCalculationLocked(order, featureFlags);

  // dirty/pending guard (mirrors offer-document-tab.js dirtyRef): local edits
  // must never be clobbered by a server refresh that raced ahead of them.
  const dirtyRef = useRef(false);
  const calcFormikRef = useRef(null);
  const calculationsRef = useRef(calculations);
  const hydratedOfferIdRef = useRef(null);
  const inFlightRef = useRef(new Set());
  const pendingSaveRef = useRef(new Map());
  const saveTokenRef = useRef(new Map());
  /** Last known optimistic-lock fields per calculationKey (survives Formik lag). */
  const lockFieldsByKeyRef = useRef(new Map());
  /** One promise chain per calculationKey so coalesced callers await the trailing save. */
  const saveChainRef = useRef(new Map());
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;
  /** Rows the user deleted this session — stale Autosave/GET must not restore. */
  const deletedRowKeysRef = useRef(new Set());
  /**
   * Bumped on «Bruk server» (and conflict open). Stale Autosave flushes and
   * in-flight persist PUTs capture the old value and abort when it changes.
   */
  const discardGenerationRef = useRef(0);
  // autosaveSuppressEpoch: bumped on placement write — cancels stale Autosave.
  // autosaveSettleEpoch: bumped after successful PUT — absorbs Formik reinit echo.
  // placementAckEpoch: bumped only after successful PUT — releases grid lock.
  // serverAdoptEpoch: bumped on «Bruk server» — remount Formik + clear grid tombstones.
  const [autosaveSuppressEpoch, setAutosaveSuppressEpoch] = useState(0);
  const autosaveSuppressEpochRef = useRef(0);
  const [autosaveSettleEpoch, setAutosaveSettleEpoch] = useState(0);
  const autosaveSettleEpochRef = useRef(0);
  const autosaveSettleFingerprintRef = useRef(null);
  const [placementAckEpoch, setPlacementAckEpoch] = useState(0);
  const placementAckEpochRef = useRef(0);
  const [serverAdoptEpoch, setServerAdoptEpoch] = useState(0);
  // After placement PUT succeeds, hold soft-sync merges briefly so a late
  // getOffer (e.g. after group CRUD) cannot snap lines back to old groups.
  const placementHoldUntilRef = useRef(0);
  /** After Use Server, block soft-sync briefly so stale Redux cannot re-strip. */
  const serverAdoptHoldUntilRef = useRef(0);

  useEffect(() => {
    calculationsRef.current = calculations;
  }, [calculations]);

  // Parent-level dirty flush: Autosave may have discarded a pending write after
  // placement suppress — still persist the mirrored calculationsRef on tab leave.
  useEffect(() => {
    if (!order?.id) return undefined;
    return registerOfferSave(order.id, {
      flush: async () => {
        if (isCalculationConflictUnresolved(calcConflictRef.current)) {
          throw new Error("calculation_conflict_unresolved");
        }
        if (!dirtyRef.current) return;
        const calc = calculationsRef.current[selectedIndexRef.current];
        if (!calc) return;
        const saved = await persistCalculationRef.current?.(calc);
        if (!saved && dirtyRef.current) {
          throw new Error("calculation_save_failed");
        }
      },
      isDirty: () =>
        dirtyRef.current ||
        isCalculationConflictUnresolved(calcConflictRef.current),
    });
  }, [order?.id]);

  const persistCalculationRef = useRef(null);

  // Hydrate once per offer id so an unrelated offer refresh doesn't wipe
  // in-progress calculation edits. Heal T4-class: empty items + nested orphans.
  useEffect(() => {
    if (!order?.id) return;
    if (hydratedOfferIdRef.current === order.id) return;
    dirtyRef.current = false;
    placementHoldUntilRef.current = 0;
    ensuringOfferIdRef.current = null;
    deletedRowKeysRef.current = new Set();
    setCalcConflictSync(null);
    const roots = pageCalculations(order.calculations).map((calc) =>
      ensureNestedLinkedAsItems(calc)
    );
    setCalculations(roots);
    hydratedOfferIdRef.current = order.id;
    // Persist shell links once when server returned nested without items.
    roots.forEach((calc) => {
      const server = (order.calculations || []).find((c) => c?.id === calc?.id);
      const serverItemCount = (server?.items || []).length;
      const localItemCount = (calc?.items || []).filter((r) => !r.delete).length;
      if (
        calc?.id &&
        localItemCount > serverItemCount &&
        (calc.nested_calculations || []).length > 0
      ) {
        persistCalculationRef.current?.(calc);
      }
    });
  }, [order?.id, order?.calculations]);

  // Soft sync from server only while pristine (no unsaved local edit pending).
  // Skip while a PUT is in flight / queued — getOffer after group save must not
  // clobber a just-moved offer_group_id before (or during) calc item persist.
  // Do NOT run ensureNestedLinkedAsItems here — that recreated deleted package
  // shells whenever Redux still had nested_calculations after a line delete.
  useEffect(() => {
    if (!order?.id || dirtyRef.current) return;
    if (inFlightRef.current.size > 0 || pendingSaveRef.current.size > 0) return;
    if (hydratedOfferIdRef.current !== order.id) return;
    if (Date.now() < serverAdoptHoldUntilRef.current) return;
    const serverRoots = pageCalculations(order.calculations).map((calc) =>
      stripDeletedCalcItems(calc, deletedRowKeysRef.current)
    );
    if (JSON.stringify(serverRoots) === JSON.stringify(calculationsRef.current)) return;
    if (Date.now() < placementHoldUntilRef.current) {
      setCalculations(
        mergeCalculationsPreservingPlacement(calculationsRef.current, serverRoots)
      );
      return;
    }
    setCalculations(serverRoots);
  }, [order?.id, order?.calculations]);

  useEffect(() => {
    if (selectedIndex >= calculations.length) {
      setSelectedIndex(Math.max(0, calculations.length - 1));
    }
  }, [calculations.length, selectedIndex]);

  const selectedCalculation =
    calculations.length === 0
      ? null
      : calculations[
          Math.min(Math.max(0, selectedIndex), calculations.length - 1)
        ] || null;
  const selectedKey = useMemo(() => {
    if (!selectedCalculation) return "empty";
    if (selectedCalculation._local_key) return `local:${selectedCalculation._local_key}`;
    if (selectedCalculation.id != null) return `id:${selectedCalculation.id}`;
    return `idx-${selectedIndex}`;
  }, [selectedCalculation, selectedIndex]);

  // Drop settle fingerprint when switching calcs so Autosave cannot baseline
  // a new Formik mount on another calc's «Bruk server» hash.
  const prevSelectedKeyRef = useRef(selectedKey);
  useEffect(() => {
    if (prevSelectedKeyRef.current === selectedKey) return;
    prevSelectedKeyRef.current = selectedKey;
    autosaveSettleFingerprintRef.current = null;
  }, [selectedKey]);

  useEffect(() => {
    const calc = selectedCalculation;
    if (!calc?.id) return undefined;
    if (hydratedIdsRef.current.has(calc.id)) return undefined;
    if ((calc.items || []).length > 0 || isModuleCalculation(calc)) {
      hydratedIdsRef.current.add(calc.id);
      return undefined;
    }
    const expectsLines =
      calc.original_calculation_id != null || Number(calc.total_amount) > 0;
    if (!expectsLines) {
      hydratedIdsRef.current.add(calc.id);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const full = await fetchOfferCalculation(calc.id);
      if (cancelled || !full || full.errors) return;
      hydratedIdsRef.current.add(calc.id);
      setCalculations((prev) =>
        prev.map((c) => {
          if (c.id !== calc.id) return c;
          if ((c.items || []).length > 0) return c;
          return {
            ...c,
            ...full,
            items: full.items || [],
            _local_key: c._local_key,
          };
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCalculation]);

  /** Local-only state update (optimistic); marks the tab dirty until saved. */
  const applyLocalCalculations = (next) => {
    dirtyRef.current = true;
    setCalculations(next);
    formik?.setFieldValue?.("calculations", next);
  };

  /**
   * Adopt server/local calc tree without marking dirty — used after a successful
   * persist (e.g. template load heal) so Customer view flush is not blocked.
   */
  const adoptSavedCalculations = (next) => {
    dirtyRef.current = false;
    setCalculations(next);
    formik?.setFieldValue?.("calculations", next);
    const focus =
      next[selectedIndexRef.current] || next[0] || null;
    if (focus) {
      autosaveSettleFingerprintRef.current =
        calculationPersistFingerprint(focus);
      autosaveSettleEpochRef.current += 1;
      setAutosaveSettleEpoch(autosaveSettleEpochRef.current);
    }
    setCalcSaveState("saved");
  };

  /** Explicit item wire shape — avoids spreading UI-only fields / circular refs. */
  const buildItemPayload = (item) => {
    const groupId = readOfferGroupId(item);
    const normalizeDec = (v) => {
      if (v === null || v === undefined || v === "") return v;
      if (typeof v === "number") return v;
      const s = String(v).trim();
      if (/^-?\d+,\d+$/.test(s)) return s.replace(",", ".");
      return v;
    };
    const mode =
      item?.hours_pricing_mode || item?.custom_fields?.hours_pricing_mode || null;
    const custom_fields = { ...(item?.custom_fields || {}) };
    if (mode) custom_fields.hours_pricing_mode = mode;
    if (Array.isArray(item?.notes)) {
      custom_fields.notes = item.notes;
    }
    const productList =
      item?.product_list || item?.custom_fields?.product_list || null;
    if (productList && Array.isArray(productList.options) && productList.options.length) {
      custom_fields.product_list = productList;
    } else {
      delete custom_fields.product_list;
    }
    const clientRowKey =
      item?.client_row_key ||
      item?.custom_fields?.client_row_key ||
      item?.draggableId ||
      null;
    if (clientRowKey) {
      custom_fields.client_row_key = String(clientRowKey);
    }
    const payload = {
      id: item?.id || undefined,
      product_id: item?.product_id ?? item?.product?.id ?? null,
      description: item?.description,
      quantity: normalizeDec(item?.quantity),
      unit_id: item?.unit_id ?? item?.unit?.id ?? null,
      sales_price: normalizeDec(item?.sales_price),
      purchase_price: normalizeDec(item?.purchase_price),
      discount: normalizeDec(item?.discount),
      surcharge: normalizeDec(item?.surcharge),
      row_order: item?.row_order,
      item_type: item?.item_type,
      line_type: item?.line_type,
      nested_calculation_id: item?.nested_calculation_id ?? null,
      divide_by_quantity: item?.divide_by_quantity || false,
      price_mode: item?.price_mode,
      hours: normalizeDec(item?.hours),
      hour_rate: normalizeDec(item?.hour_rate),
      cost_hour_rate: normalizeDec(item?.cost_hour_rate),
      hours_pricing_mode: mode,
      products: item?.products,
      product_list: productList,
      notes: item?.notes,
      show_hours: item?.show_hours,
      show_image: item?.show_image,
      show_url: item?.show_url,
      show_note_title: item?.show_note_title === true,
      note_title: item?.note_title || "",
      required_fields: item?.required_fields,
      editable_quantity: item?.editable_quantity,
      custom_fields,
      // Always send (null clears membership) so PUT cannot omit placement.
      offer_group_id: groupId,
    };
    if (item?.delete === true) payload.delete = true;
    return payload;
  };

  /**
   * Wire payload for create/update. Do NOT send nested_calculations on routine
   * saves: UpsertNestedCalculations replace-sets and would overwrite a nested
   * linjekalkyle that was just saved via Edit calc / anbefalte felter with a
   * stale parent.nested_calculations snapshot. Only sync when explicitly asked
   * (page template replace / TPL expand).
   */
  const buildCalculationPayload = (calc) => {
    const payload = {
      id: calc.id || undefined,
      offer_id: order?.id,
      original_calculation_id: calc.original_calculation_id,
      title: calc.title,
      description: calc.description,
      calculation_type:
        typeof calc.calculation_type === "string"
          ? calc.calculation_type
          : calc.calculation_type?.key,
      calculation_type_id:
        calc.calculation_type_id ||
        (typeof calc.calculation_type === "object"
          ? calc.calculation_type?.id
          : null),
      active: calc.active,
      show_hours: calc.show_hours,
      show_quantities: calc.show_quantities,
      show_products: calc.show_products,
      show_setup_only: calc.show_setup_only,
      show_quantities_for_offer_item_only:
        calc.show_quantities_for_offer_item_only,
      include_product_short_description:
        calc.include_product_short_description,
      module_enabled: calc.module_enabled,
      module_graph: calc.module_graph,
      module_result: calc.module_result,
      items: [
        ...(calc.items || []).map(buildItemPayload),
        ...(calc._deletedItems || []).map((row) => ({
          id: row.id,
          delete: true,
        })),
      ],
      ...calculationOptimisticLockFields(calc),
    };
    if (calc._syncNestedCalculations) {
      payload.nested_calculations = calc.nested_calculations || [];
    }
    return payload;
  };

  /**
   * Persist one calculation. Coalesces overlapping saves per calculation
   * (trailing edit wins) instead of firing a PUT per keystroke/add, and
   * merges the authoritative save response back into local state instead of
   * relying on a follow-up getOffer.
   *
   * Callers that hit an in-flight save await the same chain (trailing write)
   * so Autosave flush never sees a bare `undefined` and blocks Customer view.
   *
   * After save A returns, a queued edit B adopts A's lock_version / item ids
   * via reconcileQueuedItemsWithSaved so B does not falsely 409 and drop work.
   */
  const persistCalculation = async (calc, { forceConflictResolve = false } = {}) => {
    if (!order?.id || !calc) return null;
    if (calculationLocked) {
      toast.error(
        resolveOfferActionErrorMessage(
          t,
          { errors: ["offer_revision_locked"] },
          "offers.tabs.calculation_locked_save"
        )
      );
      return null;
    }
    const key = calculationKey(calc);
    if (!key) return null;

    if (
      !forceConflictResolve &&
      isCalculationConflictUnresolved(calcConflictRef.current) &&
      String(calcConflictRef.current.calculationKey) === String(key)
    ) {
      toast.error(t("offers.tabs.calculation_conflict_blocked"));
      setCalcSaveState("error");
      return null;
    }

    const keyedCalc = ensureClientRowKeys(
      stripDeletedCalcItems(calc, deletedRowKeysRef.current)
    );
    const knownLocks = lockFieldsByKeyRef.current.get(key);
    pendingSaveRef.current.set(
      key,
      adoptSavedOptimisticLockFields(keyedCalc, knownLocks)
    );
    const existingChain = saveChainRef.current.get(key);
    if (existingChain) return existingChain;

    const chain = (async () => {
      let lastSaved = null;
      /** Last calc actually sent in this chain (for final UI merge). */
      let lastSent = null;
      try {
        lastSaved = await runTrailingCalculationPersistQueue({
          getPending: () => pendingSaveRef.current.get(key) || null,
          setPending: (next) => {
            if (!next) {
              pendingSaveRef.current.delete(key);
              return;
            }
            pendingSaveRef.current.set(
              key,
              ensureClientRowKeys(
                stripDeletedCalcItems(next, deletedRowKeysRef.current)
              )
            );
          },
          persist: async (rawNext) => {
            const discardAtStart = discardGenerationRef.current;
            let nextCalc = stripDeletedCalcItems(
              rawNext,
              deletedRowKeysRef.current
            );
            const latest = calculationsRef.current.find(
              (c) =>
                calculationKey(c) === key ||
                c?._local_key === nextCalc?._local_key
            );
            if (latest?.id && !nextCalc?.id) {
              nextCalc = { ...nextCalc, id: latest.id };
            }
            lastSent = nextCalc;

            inFlightRef.current.add(key);
            const token = (saveTokenRef.current.get(key) || 0) + 1;
            saveTokenRef.current.set(key, token);
            setCalcSaveState("saving");

            try {
              // «Bruk server» bumped discard generation — do not PUT discarded draft.
              if (discardGenerationRef.current !== discardAtStart) {
                return null;
              }
              const payload = buildCalculationPayload(nextCalc);
              const isCreate = !nextCalc.id;
              const result = await dispatch(
                isCreate
                  ? createOfferCalculationAsync(payload)
                  : updateOfferCalculationAsync(payload)
              );
              const rejected = isCreate
                ? createOfferCalculationAsync.rejected.match(result)
                : updateOfferCalculationAsync.rejected.match(result);
              const saved = result.payload;

              if (rejected || !saved || saved.errors) {
                const conflictPayload = saved || result.payload;
                if (isCalculationConflictError(conflictPayload)) {
                  return conflictPayload;
                }
                setCalcSaveState("error");
                toast.error(
                  resolveOfferActionErrorMessage(
                    t,
                    saved || result.payload,
                    "offers.tabs.calculation_save_failed"
                  )
                );
                return null;
              }

              // Use Server won the race while this PUT was in flight — do not
              // merge the discarded draft's response over the adopted server view.
              if (discardGenerationRef.current !== discardAtStart) {
                return null;
              }

              if (
                saved.lock_version != null ||
                saved.updated_at != null ||
                saved.items_version != null ||
                saved.id != null
              ) {
                lockFieldsByKeyRef.current.set(
                  key,
                  adoptSavedOptimisticLockFields(
                    lockFieldsByKeyRef.current.get(key) || {},
                    saved
                  )
                );
              }

              // Trailing edit: stamp locks on UI only. Queue reconcile is owned
              // by runTrailingCalculationPersistQueue (sentItems + client_row_key).
              const hasNewerEdit = pendingSaveRef.current.has(key);
              if (hasNewerEdit) {
                setCalculations((prev) => {
                  const idx = prev.findIndex(
                    (c) =>
                      calculationKey(c) === key ||
                      (nextCalc?._local_key &&
                        c?._local_key === nextCalc._local_key) ||
                      (saved?.id != null && c?.id === saved.id)
                  );
                  if (idx === -1) return prev;
                  const stamped = adoptSavedOptimisticLockFields(
                    prev[idx],
                    saved
                  );
                  if (stamped === prev[idx]) return prev;
                  const next = [...prev];
                  next[idx] = stamped;
                  return next;
                });
              } else {
                setCalcSaveState("saved");
                setCalculations((prev) => {
                  const idx = prev.findIndex(
                    (c) =>
                      c === nextCalc ||
                      calculationKey(c) === key ||
                      (nextCalc?._local_key &&
                        c?._local_key === nextCalc._local_key) ||
                      (saved?.id != null && c?.id === saved.id)
                  );
                  if (idx === -1) return prev;
                  const local = prev[idx];
                  const localById = new Map(
                    (local?.items || [])
                      .filter((row) => row?.id != null)
                      .map((row) => [row.id, row])
                  );
                  const sentById = new Map(
                    (nextCalc?.items || [])
                      .filter((row) => row?.id != null)
                      .map((row) => [row.id, readOfferGroupId(row)])
                  );
                  const savedHasItems = Array.isArray(saved.items);
                  const savedItems = savedHasItems ? saved.items : [];
                  const sourceItems = savedHasItems
                    ? mergeSavedItemsAfterPersist({
                        savedItems,
                        sentItems: nextCalc.items,
                        deletedKeys: deletedRowKeysRef.current,
                      })
                    : stripDeletedCalcItems(
                        { items: local?.items || nextCalc.items || [] },
                        deletedRowKeysRef.current
                      ).items;
                  const mergedItems = sourceItems.map((si) => {
                    const li = localById.get(si.id);
                    const sentG = sentById.get(si.id);
                    const localG = li ? readOfferGroupId(li) : null;
                    const hasSnake = Object.prototype.hasOwnProperty.call(
                      si,
                      "offer_group_id"
                    );
                    const hasCamel = Object.prototype.hasOwnProperty.call(
                      si,
                      "offerGroupId"
                    );
                    const stableUi = {
                      draggableId:
                        li?.draggableId ||
                        si.draggableId ||
                        (si.id != null ? `id${si.id}` : undefined),
                      client_row_key:
                        li?.client_row_key ||
                        si.custom_fields?.client_row_key ||
                        li?.draggableId ||
                        si.draggableId,
                    };
                    if (!hasSnake && !hasCamel) {
                      return {
                        ...si,
                        ...stableUi,
                        offer_group_id: localG ?? sentG ?? null,
                      };
                    }
                    const serverG = readOfferGroupId(si);
                    if (serverG == null && (sentG != null || localG != null)) {
                      return {
                        ...si,
                        ...stableUi,
                        offer_group_id: sentG != null ? sentG : localG,
                      };
                    }
                    return {
                      ...si,
                      ...stableUi,
                      offer_group_id: serverG,
                    };
                  });
                  const next = [...prev];
                  next[idx] = stripDeletedCalcItems(
                    {
                      ...local,
                      ...saved,
                      items: mergedItems,
                      nested_calculations:
                        nextCalc.nested_calculations ??
                        saved.nested_calculations ??
                        local.nested_calculations,
                      _local_key: local._local_key,
                    },
                    deletedRowKeysRef.current
                  );
                  autosaveSettleFingerprintRef.current =
                    calculationPersistFingerprint(next[idx]);
                  formik?.setFieldValue?.("calculations", next);
                  return next;
                });
                placementAckEpochRef.current += 1;
                setPlacementAckEpoch(placementAckEpochRef.current);
                autosaveSettleEpochRef.current += 1;
                setAutosaveSettleEpoch(autosaveSettleEpochRef.current);
                placementHoldUntilRef.current = Date.now() + 5000;
                if (saveTokenRef.current.get(key) === token) {
                  dirtyRef.current = false;
                }
                dispatch(patchOfferCalculationSnapshot(saved));
              }

              if (saved.offer_totals) {
                dispatch(patchOfferTotals(saved.offer_totals));
              } else if (saved.total_amount != null) {
                dispatch(
                  patchOfferTotals({
                    subtotal_amount: saved.total_amount,
                    cost_total: saved.cost_total,
                    contribution_margin: saved.contribution_margin,
                    contribution_margin_percent:
                      saved.contribution_margin_percent,
                    updated_at: saved.updated_at,
                  })
                );
              }
              return saved;
            } catch (error) {
              console.error("Error saving offer calculation:", error);
              setCalcSaveState("error");
              toast.error(
                resolveOfferActionErrorMessage(
                  t,
                  error,
                  "offers.tabs.calculation_save_failed"
                )
              );
              return null;
            } finally {
              inFlightRef.current.delete(key);
            }
          },
          onConflict: async ({ sent, pending: queued }) => {
            const retainedSource =
              queued ||
              calculationsRef.current.find(
                (c) =>
                  calculationKey(c) === key ||
                  c?.id === sent?.id ||
                  (sent?._local_key && c?._local_key === sent._local_key)
              ) ||
              sent;
            pendingSaveRef.current.delete(key);
            dirtyRef.current = true;
            setCalcSaveState("error");
            // The persistent floating collaboration panel owns this conflict.
            let serverSnap = null;
            if (sent?.id) {
              try {
                const fresh = await fetchOfferCalculation(sent.id);
                if (isValidOfferCalculationSnapshot(fresh)) {
                  serverSnap = fresh;
                }
              } catch (reloadError) {
                console.error(
                  "Failed to reload calculation after conflict",
                  reloadError
                );
              }
            }
            const opened = openCalculationConflict({
              calculationKey: key,
              localCalc: retainedSource,
              serverCalc: serverSnap,
              deletedKeys: deletedRowKeysRef.current,
            });
            // Pause Autosave immediately (drop pending delete drafts) before
            // the next paint — unmount flush must not race «Bruk server».
            discardGenerationRef.current += 1;
            autosaveSuppressEpochRef.current += 1;
            setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);
            setCalcConflictSync(opened);
            lockFieldsByKeyRef.current.delete(key);
            if (opened.local) {
              setCalculations((prev) => {
                const idx = prev.findIndex(
                  (c) =>
                    calculationKey(c) === key ||
                    c?.id === sent?.id ||
                    (sent?._local_key && c?._local_key === sent._local_key)
                );
                if (idx === -1) return prev;
                const next = [...prev];
                next[idx] = {
                  ...prev[idx],
                  ...opened.local,
                  _local_key: prev[idx]._local_key,
                };
                autosaveSettleFingerprintRef.current =
                  calculationPersistFingerprint(next[idx]);
                formik?.setFieldValue?.("calculations", next);
                return next;
              });
            }
            return "break";
          },
        });
        return lastSaved;
      } finally {
        saveChainRef.current.delete(key);
        void lastSent;
      }
    })();

    saveChainRef.current.set(key, chain);
    return chain;
  };
  persistCalculationRef.current = persistCalculation;

  const resolveCalcConflictUseMine = async () => {
    const conflict = calcConflictRef.current;
    if (!conflict?.unresolved) return;
    const resolved = resolveConflictUseLocal(conflict);
    if (!resolved) return;
    const key = conflict.calculationKey;
    if (key) {
      lockFieldsByKeyRef.current.set(
        key,
        adoptSavedOptimisticLockFields({}, resolved)
      );
    }
    setCalcConflictSync(null);
    setCalculations((prev) => {
      const idx = prev.findIndex(
        (c) => calculationKey(c) === key || c?.id === resolved.id
      );
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx], ...resolved, _local_key: prev[idx]._local_key };
      formik?.setFieldValue?.("calculations", next);
      return next;
    });
    dirtyRef.current = true;
    const saved = await persistCalculation(resolved, { forceConflictResolve: true });
    if (saved?.id && !calcConflictRef.current?.unresolved) setCollaborationReviewEpoch((epoch) => epoch + 1);
  };

  const resolveCalcConflictUseServer = async () => {
    let conflict = calcConflictRef.current;
    if (!conflict?.unresolved) return;

    // If the post-409 GET failed, retry before adopting — never dismiss an
    // empty snapshot (that would resume Autosave on the stale local draft).
    if (!conflict.server) {
      const calcId =
        conflict.local?.id ??
        (typeof conflict.calculationKey === "string" &&
        conflict.calculationKey.startsWith("id:")
          ? conflict.calculationKey.slice(3)
          : null);
      let reloaded = null;
      if (calcId != null && calcId !== "") {
        try {
          const fresh = await fetchOfferCalculation(Number(calcId) || calcId);
          if (isValidOfferCalculationSnapshot(fresh)) {
            reloaded = fresh;
          }
        } catch (reloadError) {
          console.error(
            "Failed to reload calculation for Use Server",
            reloadError
          );
        }
      }
      const planned = planConflictUseServerAdoption({
        conflict,
        reloadedServer: reloaded,
        deletedKeys: deletedRowKeysRef.current,
      });
      if (
        planned.action === "keep_open" ||
        !isValidOfferCalculationSnapshot(planned.conflict?.server)
      ) {
        toast.error(t("offers.tabs.calculation_conflict_server_unavailable"));
        return;
      }
      conflict = planned.conflict;
      setCalcConflictSync(conflict);
    }

    const resolution = applyConflictUseServerResolution({
      conflict,
      deletedKeys: deletedRowKeysRef.current,
      pendingSaveByKey: pendingSaveRef.current,
      lockFieldsByKey: lockFieldsByKeyRef.current,
    });
    if (!resolution.resolved || !resolution.server) {
      toast.error(t("offers.tabs.calculation_conflict_server_unavailable"));
      return;
    }
    const { server, calculationKey: key } = resolution;
    // Discard local deletes for THIS calc only; keep other calcs' tombstones.
    deletedRowKeysRef.current = resolution.deletedKeys;
    pendingSaveRef.current = resolution.pendingSaveByKey;
    lockFieldsByKeyRef.current = resolution.lockFieldsByKey;
    // Invalidate every in-flight / debounced write from the discarded draft.
    discardGenerationRef.current += 1;
    if (key) {
      saveTokenRef.current.set(
        key,
        (saveTokenRef.current.get(key) || 0) + 1
      );
      saveChainRef.current.delete(key);
    }
    // Cancel debounced Autosave from the discarded local draft.
    autosaveSuppressEpochRef.current += 1;
    setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);

    // Fingerprint BEFORE remount so Autosave baselines on server, not local draft.
    const adopted = {
      ...server,
    };
    autosaveSettleFingerprintRef.current =
      calculationPersistFingerprint(adopted);
    autosaveSettleEpochRef.current += 1;
    setAutosaveSettleEpoch(autosaveSettleEpochRef.current);
    serverAdoptHoldUntilRef.current = Date.now() + 2500;
    // Remount Formik + grid so pendingDeleteIdsRef cannot strip restored rows.
    setServerAdoptEpoch((n) => n + 1);

    setCalcConflictSync(null);
    dirtyRef.current = false;
    setCalculations((prev) => {
      const idx = prev.findIndex(
        (c) => calculationKey(c) === key || c?.id === server.id
      );
      if (idx === -1) return prev;
      const next = [...prev];
      // Replace with pristine server — do not spread local fields / _deletedItems.
      next[idx] = {
        ...server,
        _local_key: prev[idx]._local_key,
      };
      autosaveSettleFingerprintRef.current =
        calculationPersistFingerprint(next[idx]);
      // Sync ref immediately so parent flush / soft-sync cannot race the effect.
      calculationsRef.current = next;
      formik?.setFieldValue?.("calculations", next);
      return next;
    });
    setCalcSaveState(resolution.saveState || "saved");
  };

  /** Mirror Formik → parent immediately (dirty), without hitting the API. */
  const handleLocalSyncSelected = (index, values) => {
    const stripped = stripDeletedCalcItems(
      values,
      deletedRowKeysRef.current
    );
    const next = calculationsRef.current.map((calc, i) =>
      i === index ? stripped : calc
    );
    applyLocalCalculations(next);
    dispatch(
      patchOfferTotals({
        subtotal_amount: next.reduce(
          (sum, calc) => sum + calculationSalesTotal(calc),
          0
        ),
      })
    );
  };

  const handleCommitSelected = (index, values) => {
    const stripped = stripDeletedCalcItems(
      values,
      deletedRowKeysRef.current
    );
    const next = calculationsRef.current.map((calc, i) =>
      i === index ? stripped : calc
    );
    applyLocalCalculations(next);
    return persistCalculation(stripped);
  };

  /** Sync dirty bit so getOffer soft-sync cannot race ahead of microtask persist. */
  const handlePlacementDirty = () => {
    dirtyRef.current = true;
  };

  /**
   * DnD / «Flytt til gruppe» / add-into-group: mark dirty + persist immediately
   * with the patched items array. Do not wait for Formik setFieldValue →
   * Autosave (that window is what let enableReinitialize / getOffer soft-sync
   * wipe offer_group_id before the debounced PUT). Also bump suppressEpoch so
   * any already-scheduled Autosave with pre-move values is discarded.
   */
  const handleItemsPatched = (items) => {
    const idx = selectedIndex;
    const current = calculationsRef.current[idx];
    if (!current) return;
    const linkedNestedIds = new Set(
      (items || [])
        .map((row) => row?.nested_calculation_id ?? row?.nested_calculation?.id)
        .filter((id) => id != null)
        .map(String)
    );
    deletedRowKeys(current.items, items).forEach((key) => {
      deletedRowKeysRef.current.add(key);
    });
    // Drop nested calcs whose package shell was removed — otherwise a later
    // ensureNestedLinkedAsItems / soft-sync resurrects the deleted line.
    const nextNested = (current.nested_calculations || []).filter((n) =>
      n?.id != null ? linkedNestedIds.has(String(n.id)) : false
    );
    const newTombstones = deletedItemTombstones(current.items, items);
    const existingTombstones = current._deletedItems || [];
    const seenTombstoneIds = new Set(
      existingTombstones.map((row) => String(row.id))
    );
    const mergedTombstones = [
      ...existingTombstones,
      ...newTombstones.filter((row) => !seenTombstoneIds.has(String(row.id))),
    ];
    // Keep `_deletedItems` on local state so later Autosave (items already
    // without the row) still sends delete:true — stripDeletedCalcItems no
    // longer dumps the shared session Set onto every calc.
    const values = {
      ...current,
      items,
      nested_calculations: nextNested,
      _deletedItems: mergedTombstones,
    };
    const next = calculationsRef.current.map((calc, i) =>
      i === idx ? values : calc
    );
    autosaveSuppressEpochRef.current += 1;
    setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);
    applyLocalCalculations(next);
    return persistCalculation(values);
  };

  const openNestedSourceChooser = (mode = "line", row = null) => {
    setAttachTargetRow(row);
    setTemplatePickerMode(mode);
    setSourceChooserOpen(true);
  };

  const handleInsertTemplateAsLine = () => {
    openNestedSourceChooser("line", null);
  };

  const handleLoadPageTemplate = () => {
    setAttachTargetRow(null);
    setTemplatePickerMode("replace");
    setCalculationSelectorOpen(true);
  };

  const applyPageTemplate = async (calcData, parent) => {
    const expanded = expandTemplateByUnits(calcData);
    const snapshot = parent;
    const { offerGroupId: importGroupId, groupIdMap } =
      await resolveTemplateImportGrouping(expanded || calcData);
    const nextValues = {
      ...buildReplaceFromTemplateValues(parent, expanded, {
        offerGroupId: importGroupId,
        groupIdMap,
      }),
      // Explicit: page template replace must upsert the nested tree once.
      _syncNestedCalculations: true,
    };
    // Block Autosave from racing a destructive replace (delete-all + recreate).
    autosaveSuppressEpochRef.current += 1;
    setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);
    applyLocalCalculations(
      calculationsRef.current.map((calc, i) =>
        i === selectedIndex ? nextValues : calc
      )
    );
    const saved = await persistCalculation(nextValues);
    if (!saved) {
      // Restore prior lines — never leave Kalkyle empty after a failed import.
      applyLocalCalculations(
        calculationsRef.current.map((calc, i) =>
          i === selectedIndex ? snapshot : calc
        )
      );
      toast.error(t("offers.tabs.calculation_load_failed"));
      return;
    }
    // API LinkOrphan + response may still need shells remapped onto items.
    const healed = ensureNestedLinkedAsItems({
      ...saved,
      nested_calculations:
        saved.nested_calculations || nextValues.nested_calculations || [],
      items: saved.items || [],
    });
    if (
      (healed.items || []).length > (saved.items || []).length
    ) {
      const healedSaved = await persistCalculation({
        ...healed,
        _syncNestedCalculations: false,
      });
      if (!healedSaved) {
        // Keep healed shells visible, but leave dirty so flush can retry.
        applyLocalCalculations(
          calculationsRef.current.map((calc, i) =>
            i === selectedIndex ? healed : calc
          )
        );
        toast.error(t("offers.tabs.calculation_save_failed"));
        return;
      }
      // Trailing persist already merged + cleared dirty / settle fingerprint.
    } else {
      // Do NOT re-mark dirty after a successful save — that blocked Customer
      // view (Autosave flush threw calculation_save_failed on coalesce).
      adoptSavedCalculations(
        calculationsRef.current.map((calc, i) =>
          i === selectedIndex ? healed : calc
        )
      );
    }
    toast.success(t("offers.tabs.calculation_template_loaded"));
  };

  const handleAttachNestedTemplate = (row, template) => {
    if (template?.id) {
      handleCalculationSelect([template], "attach", row);
      return;
    }
    openNestedSourceChooser("attach", row);
  };

  const waitForParentId = async () => {
    for (let i = 0; i < 25; i += 1) {
      const current = calculationsRef.current[selectedIndex];
      if (current?.id) return current;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return calculationsRef.current[selectedIndex];
  };

  const copyTemplateAsNestedCalc = async (
    calcData,
    parentId,
    { offerGroupId = null, groupIdMap = null } = {}
  ) => {
    const expanded = expandTemplateByUnits(calcData);
    if (!expanded || expanded.errors || expanded.id == null) return null;
    const payload = buildNestedCopyFromTemplatePayload(expanded, {
      parentId,
      offerId: order?.id,
      offerGroupId,
      groupIdMap,
    });
    const result = await dispatch(createOfferCalculationAsync(payload));
    if (
      createOfferCalculationAsync.rejected.match(result) ||
      result.payload?.errors ||
      !result.payload?.id
    ) {
      return null;
    }
    return { ...result.payload, items: result.payload.items || [] };
  };

  const nestedLineFromCopy = (
    savedCopy,
    rowOrder,
    existingRow = null,
    { offerGroupId = undefined } = {}
  ) =>
    nestedLineFromSavedCopy(savedCopy, rowOrder, existingRow, {
      offerGroupId:
        offerGroupId !== undefined
          ? offerGroupId
          : existingRow
            ? readOfferGroupId(existingRow)
            : null,
    });

  const attachSavedNestedLine = (
    savedCopy,
    mode,
    targetRow,
    { offerGroupId = null } = {}
  ) => {
    const currentItems = [
      ...(calculationsRef.current[selectedIndex]?.items || []),
    ];
    let nextItems;
    if (mode === "attach" && targetRow) {
      const idx = currentItems.findIndex(
        (row) =>
          row === targetRow ||
          (targetRow.row_order != null && row.row_order === targetRow.row_order)
      );
      const line = nestedLineFromCopy(
        savedCopy,
        idx >= 0 ? idx : currentItems.length,
        idx >= 0 ? currentItems[idx] : targetRow,
        {
          offerGroupId:
            offerGroupId != null
              ? offerGroupId
              : readOfferGroupId(
                  idx >= 0 ? currentItems[idx] : targetRow
                ),
        }
      );
      if (idx >= 0) {
        nextItems = currentItems.map((row, i) => (i === idx ? line : row));
      } else {
        nextItems = [...currentItems, line];
      }
    } else {
      nextItems = [
        ...currentItems,
        nestedLineFromCopy(savedCopy, currentItems.length, null, {
          offerGroupId,
        }),
      ];
    }
    handleItemsPatched(nextItems);
    if (!savedCopy?.id) {
      toast.error(t("offers.tabs.calculation_load_failed"));
      return;
    }
    toast.success(t("offers.tabs.calculation_line_added"));
    setNestedEditorFocusRecommended(false);
    setNestedEditorId(savedCopy.id);
  };

  const finishNestedFromTemplate = async (calcData, parent, mode, targetRow) => {
    const expanded = expandTemplateByUnits(calcData);
    const { offerGroupId: importGroupId, groupIdMap } =
      await resolveTemplateImportGrouping(expanded || calcData);
    const savedCopy = await copyTemplateAsNestedCalc(calcData, parent.id, {
      offerGroupId: importGroupId,
      groupIdMap,
    });
    if (!savedCopy) {
      toast.error(t("offers.tabs.calculation_load_failed"));
      return;
    }
    attachSavedNestedLine(savedCopy, mode, targetRow, {
      offerGroupId: importGroupId,
    });
  };

  const finishBlankNested = async (parent, mode, targetRow) => {
    const payload = buildBlankNestedCalculationPayload({
      parentId: parent.id,
      offerId: order?.id || parent.offer_id || null,
      title: t("offer.calculation.nested.blank_title"),
    });
    const result = await dispatch(createOfferCalculationAsync(payload));
    if (
      createOfferCalculationAsync.rejected.match(result) ||
      result.payload?.errors ||
      !result.payload?.id
    ) {
      toast.error(t("offers.tabs.calculation_load_failed"));
      return;
    }
    const savedCopy = { ...result.payload, items: result.payload.items || [] };
    attachSavedNestedLine(savedCopy, mode, targetRow);
  };

  const handleChooseNestedNew = async () => {
    if (sourceChooserBusy || addingFromTemplateRef.current) return;
    addingFromTemplateRef.current = true;
    setSourceChooserBusy(true);
    try {
      let parent = calculationsRef.current[selectedIndex];
      if (!parent) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return;
      }
      if (!parent.id) {
        await persistCalculation(parent);
        parent = await waitForParentId();
      }
      if (!parent?.id) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return;
      }
      const mode = templatePickerMode === "attach" ? "attach" : "line";
      setSourceChooserOpen(false);
      await finishBlankNested(parent, mode, attachTargetRow);
      setAttachTargetRow(null);
    } finally {
      setSourceChooserBusy(false);
      addingFromTemplateRef.current = false;
    }
  };

  const handleChooseNestedExisting = () => {
    setSourceChooserOpen(false);
    setCalculationSelectorOpen(true);
  };

  const handleCalculationSelect = async (
    selectedCalculations,
    modeOverride,
    rowOverride
  ) => {
    const templateId = selectedCalculations?.[0]?.id;
    if (!templateId || addingFromTemplateRef.current) return;
    addingFromTemplateRef.current = true;
    const mode = modeOverride || templatePickerMode || "line";
    const targetRow = rowOverride !== undefined ? rowOverride : attachTargetRow;
    try {
      let parent = calculationsRef.current[selectedIndex];
      if (!parent) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return;
      }
      if (!parent.id) {
        await persistCalculation(parent);
        parent = await waitForParentId();
      }
      if (!parent?.id) {
        toast.error(t("offers.tabs.calculation_save_failed"));
        return;
      }

      if (mode === "replace") {
        const hasContent =
          (parent.items || []).some((row) => !row.delete) ||
          parent.module_enabled ||
          parent.module_graph;
        if (
          hasContent &&
          !window.confirm(t("offers.tabs.calculation_replace_template_confirm"))
        ) {
          return;
        }
        const calcData = await fetchOfferCalculation(templateId);
        if (!calcData || calcData.errors || calcData.id == null) {
          toast.error(t("offers.tabs.calculation_load_failed"));
          return;
        }
        setCalculationSelectorOpen(false);
        setAttachTargetRow(null);
        if (templateNeedsWizard(calcData, t)) {
          setTemplateWizard({ calcData, mode: "replace", parent });
          return;
        }
        await applyPageTemplate(calcData, parent);
        return;
      }

      const calcData = await fetchOfferCalculation(templateId);
      if (!calcData || calcData.errors || calcData.id == null) {
        toast.error(t("offers.tabs.calculation_load_failed"));
        return;
      }
      setCalculationSelectorOpen(false);
      setAttachTargetRow(null);
      if (templateNeedsWizard(calcData, t)) {
        setTemplateWizard({
          calcData,
          mode,
          parent,
          targetRow,
        });
        return;
      }
      await finishNestedFromTemplate(calcData, parent, mode, targetRow);
    } catch (error) {
      console.error("Error fetching calculation:", error);
      toast.error(t("offers.tabs.calculation_load_failed"));
    } finally {
      addingFromTemplateRef.current = false;
    }
  };

  /** One blank offer calculation page — created silently when the offer has none. */
  const ensurePageCalculation = async () => {
    if (!order?.id) return;
    if (calculationLocked) return;
    if (calculationsRef.current.length > 0) return;
    const blank = {
      id: null,
      offer_id: order.id,
      original_calculation_id: null,
      is_copy: false,
      title: t("offers.tabs.calculation_page_title"),
      description: "",
      calculation_type: "standard",
      active: true,
      show_hours: true,
      show_quantities: true,
      show_products: true,
      show_setup_only: false,
      show_quantities_for_offer_item_only: false,
      include_product_short_description: false,
      module_enabled: false,
      module_graph: null,
      module_result: null,
      items: [],
      nested_calculations: [],
      _local_key: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    applyLocalCalculations([blank]);
    setSelectedIndex(0);
    await persistCalculation(blank);
  };

  const handleNestedModulePersist = async (nested) => {
    if (!nested?.id || !order?.id) return null;
    const result = await dispatch(
      updateOfferCalculationAsync({
        id: nested.id,
        offer_id: order.id,
        title: nested.title,
        description: nested.description,
        module_enabled: nested.module_enabled !== false,
        module_graph: nested.module_graph,
        module_result: nested.module_result,
        // Never send nested_calculations here — replace-set would wipe children.
      })
    );
    if (
      updateOfferCalculationAsync.rejected.match(result) ||
      result.payload?.errors
    ) {
      toast.error(t("offers.tabs.calculation_save_failed"));
      return null;
    }
    const saved = result.payload;
    if (saved?.offer_totals) {
      dispatch(patchOfferTotals(saved.offer_totals));
    }
    const current = calculationsRef.current[selectedIndex];
    if (!current || !saved?.id) return saved;
    const sameId = (a, b) =>
      a != null && b != null && String(a) === String(b);
    const unitSales =
      saved.total_amount != null ? Number(saved.total_amount) : null;
    const unitCost = Number(saved.purchase_amount ?? saved.cost_total ?? NaN);
    const nextItems = (current.items || []).map((row) => {
      if (
        !sameId(row.nested_calculation_id, saved.id) &&
        !sameId(row.nested_calculation?.id, saved.id)
      ) {
        return row;
      }
      const qty = Number(row.quantity);
      const effectiveQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
      const sales =
        unitSales != null && Number.isFinite(unitSales)
          ? unitSales
          : Number(row.sales_price) || 0;
      const cost = Number.isFinite(unitCost)
        ? unitCost
        : Number(row.purchase_price) || 0;
      const patched = {
        ...row,
        nested_calculation_id: saved.id,
        nested_calculation: {
          ...(row.nested_calculation || {}),
          ...saved,
          divide_by_quantity: row.nested_calculation?.divide_by_quantity,
        },
        sales_price: sales,
        purchase_price: cost,
      };
      return {
        ...patched,
        line_total: lineAmount(patched),
        cost_total: cost * effectiveQty,
      };
    });
    const nextNestedList = (current.nested_calculations || []).map((n) =>
      sameId(n?.id, saved.id) ? { ...n, ...saved } : n
    );
    const values = {
      ...current,
      items: nextItems,
      nested_calculations: nextNestedList,
      total_amount: calculationSalesTotal({ items: nextItems }),
    };
    const next = calculationsRef.current.map((calc, i) =>
      i === selectedIndex ? values : calc
    );
    autosaveSuppressEpochRef.current += 1;
    setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);
    applyLocalCalculations(next);
    dispatch(
      patchOfferTotals({
        subtotal_amount: next.reduce(
          (sum, calc) => sum + calculationSalesTotal(calc),
          0
        ),
      })
    );
    return persistCalculation(values);
  };

  useEffect(() => {
    if (!order?.id) return;
    if (hydratedOfferIdRef.current !== order.id) return;
    if (calculations.length > 0) return;
    if (ensuringOfferIdRef.current === order.id) return;
    ensuringOfferIdRef.current = order.id;
    ensurePageCalculation();
  }, [order?.id, calculations.length]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-slate-50">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
        <div className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
          {selectedCalculation?.title || t("offers.tabs.calculation_page_title")}
        </div>
        <div className="flex items-center gap-2">
          {selectedCalculation && !calculationLocked ? (
            <CalculationTemplateActions
              calculation={selectedCalculation}
              featureFlags={featureFlags}
              onSaved={() => {}}
            />
          ) : null}
        </div>
      </div>

      {calculationLocked ? (
        <div
          className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2.5 sm:px-4"
          role="status"
        >
          <p className="text-[13px] font-semibold text-amber-950">
            {t("offers.tabs.calculation_locked_title")}
          </p>
          <p className="mt-0.5 text-[12.5px] text-amber-900">
            {t("offers.tabs.calculation_locked_hint")}
          </p>
        </div>
      ) : null}

      <RecordCollaborationPanel key={`${selectedCalculation?.id}:${serverAdoptEpoch}:${collaborationReviewEpoch}`} record={selectedCalculation} recordType="calculation" section="items"
        editing={dirtyRef.current || calcSaveState === "saving"} visible={!nestedEditorId}
        saving={gridEditorOpen || inFlightRef.current.size > 0 || pendingSaveRef.current.size > 0 || sourceChooserBusy || calculationSelectorOpen || !!templateWizard || presentationOpen}
        canRefresh={() => !dirtyRef.current && (!calcFormikRef.current || calculationPersistFingerprint(calcFormikRef.current.values) === calculationPersistFingerprint(calcFormikRef.current.initialValues)) && inFlightRef.current.size === 0 && pendingSaveRef.current.size === 0 && !calcConflictRef.current?.unresolved}
        onRefresh={async () => {
          const fresh = await loadRecordSnapshot("calculation", selectedCalculation.id);
          return { apply: () => {
            const key = calculationKey(fresh);
            const resolution = applyConflictUseServerResolution({
              conflict: { unresolved: true, server: fresh, local: selectedCalculation, calculationKey: key },
              deletedKeys: deletedRowKeysRef.current, pendingSaveByKey: pendingSaveRef.current, lockFieldsByKey: lockFieldsByKeyRef.current,
            });
            if (!resolution.resolved) throw new Error("Invalid calculation snapshot");
            deletedRowKeysRef.current = resolution.deletedKeys;
            lockFieldsByKeyRef.current = resolution.lockFieldsByKey;
            const next = calculationsRef.current.map(c => c.id === fresh.id ? { ...fresh, _local_key: c._local_key } : c);
            calculationsRef.current = next;
            autosaveSuppressEpochRef.current += 1;
            setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);
            serverAdoptHoldUntilRef.current = Date.now() + 2500;
            adoptSavedCalculations(next);
            // Re-seed the pristine grid/Autosave together, just like Use Server.
            // A grid effect on a still-old Formik render can otherwise echo old
            // rows back into the newly loaded snapshot and mark it pending.
            setServerAdoptEpoch(n => n + 1);
            calcFormikRef.current?.resetForm({ values: next.find(c => c.id === fresh.id) });
            dispatch(patchOfferCalculationSnapshot(fresh));
          } };
        }}
        needsReview={Boolean(calcConflict?.unresolved)}
        errorMessage={calcConflict?.unresolved ? t("offers.tabs.calculation_conflict_resolve_hint") : undefined}
        onReload={() => {
          discardGenerationRef.current += 1;
          dirtyRef.current = false;
          pendingSaveRef.current.clear();
          window.location.reload();
        }}
        recoveryActions={calcConflict?.unresolved ? (<div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100"
              onClick={() => {
                resolveCalcConflictUseServer().catch(() => {});
              }}
            >
              {t("offers.workspace.conflict_use_server")}
            </button>
            <button
              type="button"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
              onClick={() => {
                resolveCalcConflictUseMine().catch(() => {});
              }}
            >
              {t("offers.tabs.calculation_conflict_use_mine")}
            </button>
          </div>) : undefined}
      />

      {selectedCalculation ? (
        <div
          className={[
            "flex min-h-0 flex-1 flex-col overflow-hidden",
            calculationLocked ? "pointer-events-none select-none opacity-80" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-readonly={calculationLocked || undefined}
        >
          <Formik
            innerRef={calcFormikRef}
            key={`${selectedKey}:adopt${serverAdoptEpoch}`}
            enableReinitialize
            initialValues={selectedCalculation}
            onSubmit={() => {}}
          >
            {(calcFormik) => (
              <div className="flex h-full min-h-0 flex-1 flex-col">
                {!calculationLocked ? (
                  <CalculationAutosave
                    offerId={order?.id}
                    values={calcFormik.values}
                    selectedIndex={selectedIndex}
                    onLocalSync={handleLocalSyncSelected}
                    onCommit={handleCommitSelected}
                    onSaveState={setCalcSaveState}
                    suppressEpoch={autosaveSuppressEpoch}
                    suppressEpochRef={autosaveSuppressEpochRef}
                    settleEpoch={autosaveSettleEpoch}
                    settleFingerprintRef={autosaveSettleFingerprintRef}
                    paused={Boolean(calcConflict?.unresolved)}
                    discardGenerationRef={discardGenerationRef}
                  />
                ) : null}
                <OfferCalculationWorkspace
                  onLocalEditorChange={setGridEditorOpen}
                  {...calcFormik}
                  name="items"
                  calculation={calcFormik.values}
                  calculationV2Enabled={calculationV2Enabled}
                  authoritativeCalculation={calcFormik.values}
                  isLoading={false}
                  offer={order}
                  saveStatus={calcSaveState}
                  onPlacementDirty={calculationLocked ? null : handlePlacementDirty}
                  onItemsPatched={calculationLocked ? null : handleItemsPatched}
                  placementEpoch={placementAckEpoch}
                  serverAdoptEpoch={serverAdoptEpoch}
                  onUseTemplate={calculationLocked ? null : handleInsertTemplateAsLine}
                  onLoadPageTemplate={calculationLocked ? null : handleLoadPageTemplate}
                  onOpenPresentation={() => setPresentationOpen(true)}
                  onEditNestedCalculation={
                    calculationLocked
                      ? null
                      : (nested, opts) => {
                          if (!nested?.id) return;
                          setNestedEditorFocusRecommended(
                            opts?.focusRecommended === true
                          );
                          setNestedEditorId(nested.id);
                        }
                  }
                  onAttachNestedTemplate={
                    calculationLocked ? null : handleAttachNestedTemplate
                  }
                  onNestedModulePersist={
                    calculationLocked ? null : handleNestedModulePersist
                  }
                />
              </div>
            )}
          </Formik>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
          <p className="text-sm text-slate-500">
            {calculationLocked
              ? t("offers.tabs.calculation_locked_empty")
              : t("offers.tabs.calculation_creating")}
          </p>
        </div>
      )}

      <CalculationNestedSourceDialog
        open={sourceChooserOpen}
        busy={sourceChooserBusy}
        onClose={() => {
          if (sourceChooserBusy) return;
          setSourceChooserOpen(false);
          setAttachTargetRow(null);
        }}
        onChooseNew={handleChooseNestedNew}
        onChooseExisting={handleChooseNestedExisting}
      />

      <Dialog
        open={calculationSelectorOpen}
        onClose={() => setCalculationSelectorOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {templatePickerMode === "replace"
            ? t("offers.tabs.calculation_select_template_replace")
            : templatePickerMode === "attach"
              ? t("offers.tabs.calculation_select_template_attach")
              : t("offers.tabs.calculation_select_template_as_line")}
        </DialogTitle>
        <DialogContent>
          <ModernCalculationSelector
            open={calculationSelectorOpen}
            selectedCalculations={[]}
            onChange={handleCalculationSelect}
            onClose={() => setCalculationSelectorOpen(false)}
            multiple={false}
            showHeader={false}
            createTemplatePath={null}
          />
        </DialogContent>
      </Dialog>

      <OfferTemplateWizardDialog
        open={Boolean(templateWizard)}
        templateCalc={templateWizard?.calcData}
        onClose={() => setTemplateWizard(null)}
        onConfirm={async (answeredCalc) => {
          const ctx = templateWizard;
          setTemplateWizard(null);
          if (!ctx?.parent || !answeredCalc) return;
          if (ctx.mode === "replace") {
            await applyPageTemplate(answeredCalc, ctx.parent);
            return;
          }
          await finishNestedFromTemplate(
            answeredCalc,
            ctx.parent,
            ctx.mode,
            ctx.targetRow
          );
        }}
      />

      <OfferPresentationRulesDialog
        open={presentationOpen}
        onClose={() => setPresentationOpen(false)}
        offer={order}
        onSaved={(_savedOffer, meta) => {
          setPresentationOpen(false);
          dispatch(getOfferAsync(order.id));
          if (!meta?.calcIds?.length) return;
          const flag = Boolean(meta.include_product_short_description);
          const patchCalc = (calc) => {
            if (!calc) return calc;
            const next = { ...calc };
            if (meta.calcIds.includes(calc.id)) {
              next.include_product_short_description = flag;
            }
            if (Array.isArray(next.nested_calculations)) {
              next.nested_calculations = next.nested_calculations.map(patchCalc);
            }
            if (Array.isArray(next.items)) {
              next.items = next.items.map((item) =>
                item?.nested_calculation
                  ? {
                      ...item,
                      nested_calculation: patchCalc(item.nested_calculation),
                    }
                  : item
              );
            }
            return next;
          };
          setCalculations((prev) => prev.map(patchCalc));
        }}
      />

      <NestedCalculationEditorDialog
        open={nestedEditorId != null}
        calculationId={nestedEditorId}
        offer={order}
        calculationV2Enabled={calculationV2Enabled}
        initialTab="products"
        focusRecommendedFields={nestedEditorFocusRecommended}
        onClose={() => {
          setNestedEditorId(null);
          setNestedEditorFocusRecommended(false);
        }}
        onSaved={async (saved) => {
          const current = calculationsRef.current[selectedIndex];
          if (!current || !saved?.id) return;
          const sameId = (a, b) =>
            a != null && b != null && String(a) === String(b);
          const unitSales =
            saved.total_amount != null ? Number(saved.total_amount) : null;
          const unitCost = Number(
            saved.purchase_amount ?? saved.cost_total ?? NaN
          );
          const nextItems = (current.items || []).map((row) => {
            if (
              !sameId(row.nested_calculation_id, saved.id) &&
              !sameId(row.nested_calculation?.id, saved.id)
            ) {
              return row;
            }
            const qty = Number(row.quantity);
            const effectiveQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
            const sales =
              unitSales != null && Number.isFinite(unitSales)
                ? unitSales
                : Number(row.sales_price) || 0;
            const cost =
              Number.isFinite(unitCost) ? unitCost : Number(row.purchase_price) || 0;
            const patched = {
              ...row,
              nested_calculation_id: saved.id,
              nested_calculation: {
                ...(row.nested_calculation || {}),
                ...saved,
                divide_by_quantity: row.nested_calculation?.divide_by_quantity,
              },
              sales_price: sales,
              purchase_price: cost,
              description: row.description || saved.title,
            };
            return {
              ...patched,
              line_total: lineAmount(patched),
              cost_total: cost * effectiveQty,
            };
          });
          const nextNestedList = (current.nested_calculations || []).map((n) =>
            sameId(n?.id, saved.id) ? { ...n, ...saved } : n
          );
          const idx = selectedIndex;
          const values = {
            ...current,
            items: nextItems,
            nested_calculations: nextNestedList,
            total_amount: calculationSalesTotal({ items: nextItems }),
          };
          const next = calculationsRef.current.map((calc, i) =>
            i === idx ? values : calc
          );
          autosaveSuppressEpochRef.current += 1;
          setAutosaveSuppressEpoch(autosaveSuppressEpochRef.current);
          applyLocalCalculations(next);
          if (saved.offer_totals) {
            dispatch(patchOfferTotals(saved.offer_totals));
          }
          dispatch(
            patchOfferTotals({
              subtotal_amount: next.reduce(
                (sum, calc) => sum + calculationSalesTotal(calc),
                0
              ),
            })
          );
          return persistCalculation(values);
        }}
      />
    </div>
  );
}
